'use client';

import { c as _c } from "react/compiler-runtime";
import NextImage, { type ImageProps, type StaticImageData } from 'next/image';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { IMAGE_PLACEHOLDER } from '@/constants';
const CDN_HOST = 'cdn.welfarehello.com';
const loadedImageSources = new Set<string>();

/** 외부 이미지 (CDN이 아닌) 여부 확인 */
const isExternalNonCdnImage = (src: string | StaticImageData | undefined): boolean => {
  if (!src || typeof src !== 'string') return false;
  if (!src.startsWith('http://') && !src.startsWith('https://')) return false;
  try {
    const url = new URL(src);
    return !url.hostname.includes(CDN_HOST);
  } catch {
    return false;
  }
};

//* next/image는 sizes에 vw가 없으면 후보 폭 16개를 전부 srcset에 싣는다 — 슬롯이 모두 px면 최대 폭 하나로 1x·2x만 내보낸다
const getFixedSlotWidth = (sizes: string | undefined) => {
  const slotWidths = (sizes ?? '').split(',').map(part => /^(\d+)px$/.exec(part.trim().split(/\s+/).at(-1) ?? ''));
  if (!sizes || slotWidths.some(match => !match)) {
    return;
  }
  return Math.max(...slotWidths.map(match => Number(match?.[1])));
};
const FILL_STYLE = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%'
} as const;
const getImageCacheKey = (src: string | StaticImageData | undefined) => {
  if (!src) return;
  return typeof src === 'string' ? src : src.src;
};
interface CustomImageBaseProps extends Omit<ImageProps, 'src' | 'className' | 'onError' | 'onLoad' | 'width' | 'height' | 'fill'> {
  src?: string | StaticImageData;
  alt: string;
  fallbackSrc?: string;
  className?: string;
  onError?: (error: React.SyntheticEvent<HTMLImageElement>) => void;
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
}
type CustomImageProps = (CustomImageBaseProps & {
  fill: true;
  width?: number;
  height?: number;
}) | (CustomImageBaseProps & {
  fill?: false;
  width: number;
  height: number;
});
export const Image = memo(t0 => {
  const $ = _c(42);
  let alt;
  let className;
  let fallbackSrc;
  let loading;
  let onError;
  let onLoad;
  let preload;
  let props;
  let quality;
  let src;
  let t1;
  let unoptimized;
  if ($[0] !== t0) {
    const {
      src: t2,
      alt: t3,
      fallbackSrc: t4,
      quality: t5,
      loading: t6,
      className: t7,
      onError: t8,
      onLoad: t9,
      preload: t10,
      unoptimized: t11,
      ...t12
    } = t0;
    src = t2;
    alt = t3;
    className = t7;
    onError = t8;
    onLoad = t9;
    preload = t10;
    unoptimized = t11;
    props = t12;
    fallbackSrc = t4 === undefined ? IMAGE_PLACEHOLDER : t4;
    quality = t5 === undefined ? 50 : t5;
    loading = t6 === undefined ? "lazy" : t6;
    t1 = props.fill ? getFixedSlotWidth(props.sizes) : undefined;
    $[0] = t0;
    $[1] = alt;
    $[2] = className;
    $[3] = fallbackSrc;
    $[4] = loading;
    $[5] = onError;
    $[6] = onLoad;
    $[7] = preload;
    $[8] = props;
    $[9] = quality;
    $[10] = src;
    $[11] = t1;
    $[12] = unoptimized;
  } else {
    alt = $[1];
    className = $[2];
    fallbackSrc = $[3];
    loading = $[4];
    onError = $[5];
    onLoad = $[6];
    preload = $[7];
    props = $[8];
    quality = $[9];
    src = $[10];
    t1 = $[11];
    unoptimized = $[12];
  }
  const fixedSlotWidth = t1;
  let t2;
  if ($[13] !== fixedSlotWidth || $[14] !== props) {
    t2 = fixedSlotWidth ? {
      fill: false,
      sizes: undefined,
      width: fixedSlotWidth,
      height: fixedSlotWidth,
      style: {
        ...FILL_STYLE,
        ...props.style
      }
    } : {};
    $[13] = fixedSlotWidth;
    $[14] = props;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const sizingProps = t2;
  const shouldPreload = preload;
  const [hasError, setHasError] = useState(false);
  const displaySrc = hasError ? fallbackSrc : src || fallbackSrc;
  let t3;
  if ($[16] !== displaySrc) {
    t3 = getImageCacheKey(displaySrc);
    $[16] = displaySrc;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const cacheKey = t3;
  const hasLoadedBefore = cacheKey ? loadedImageSources.has(cacheKey) : false;
  let t4;
  if ($[18] !== displaySrc || $[19] !== unoptimized) {
    t4 = unoptimized ?? isExternalNonCdnImage(displaySrc);
    $[18] = displaySrc;
    $[19] = unoptimized;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const shouldUnoptimize = t4;
  let t5;
  if ($[21] !== hasError || $[22] !== onError) {
    t5 = error => {
      if (!hasError) {
        setHasError(true);
        onError?.(error);
      }
    };
    $[21] = hasError;
    $[22] = onError;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  const handleError = t5;
  let t6;
  if ($[24] !== cacheKey || $[25] !== onLoad) {
    t6 = event => {
      if (cacheKey) {
        loadedImageSources.add(cacheKey);
      }
      onLoad?.(event);
    };
    $[24] = cacheKey;
    $[25] = onLoad;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const handleLoad = t6;
  const resolvedLoading = preload || loading !== "lazy" || !hasLoadedBefore ? loading : "eager";
  let t7;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setHasError(false);
    };
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== src) {
    t8 = [src];
    $[28] = src;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  useEffect(t7, t8);
  const t9 = shouldPreload ? undefined : resolvedLoading;
  let t10;
  if ($[30] !== alt || $[31] !== className || $[32] !== displaySrc || $[33] !== handleError || $[34] !== handleLoad || $[35] !== props || $[36] !== quality || $[37] !== shouldPreload || $[38] !== shouldUnoptimize || $[39] !== sizingProps || $[40] !== t9) {
    t10 = <NextImage quality={quality} loading={t9} preload={shouldPreload} unoptimized={shouldUnoptimize} {...props} {...sizingProps} alt={alt} className={className} src={displaySrc} onError={handleError} onLoad={handleLoad} />;
    $[30] = alt;
    $[31] = className;
    $[32] = displaySrc;
    $[33] = handleError;
    $[34] = handleLoad;
    $[35] = props;
    $[36] = quality;
    $[37] = shouldPreload;
    $[38] = shouldUnoptimize;
    $[39] = sizingProps;
    $[40] = t9;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  return t10;
});
Image.displayName = 'Image';